/**
 * Omnibar imperative bridge
 *
 * Module-level handles that let surfaces *outside* `<DraftBar>` reach
 * back into it without prop-drilling or React context:
 *
 *   - `registerOmnibarInput` / `getOmnibarInput` / `focusOmnibarInput`
 *     — `<DraftBar>` registers its draft input element on mount; the
 *     global `⌘D` / `⌘F` shortcuts (see `use-omnibar-focus-shortcut.ts`)
 *     focus it imperatively when invoked.
 *   - `registerOmnibarExpand` / `getOmnibarExpand` — `<DraftBar>`
 *     publishes its `isExpanded` state + setter so the global `⌘D`
 *     cycle (Add → Focus → Add → …) can read the current state and
 *     flip it without lifting the state out of the bar.
 *   - `registerOmnibarMinimize` / `minimizeOmnibar` — `<DraftBar>`
 *     registers a "tear the bar down" callback (clear textarea text,
 *     blur whatever's focused inside the host, reset stream-fade,
 *     flip the mode back to `add`); the Ask-mode filter strip calls
 *     it when a result is activated so navigating away leaves the bar
 *     in its idle Draft state instead of holding stale query text, a
 *     focused result card, or a sticky Ask mode the user has to
 *     manually exit.
 *   - `registerOmnibarFirstResultOpener` / `openOmnibarFirstResult` —
 *     the Ask-mode filter strip publishes an "open my top match"
 *     callback so `<DraftBar>`'s Ask-mode Enter handler can hijack
 *     plain Enter when there's at least one instant result. The first
 *     result is then the natural Enter target (autocomplete /
 *     command-palette pattern); when no match is registered, Enter
 *     falls through to the assistant query path.
 *
 * All registrations are intentionally module-local rather than parts
 * of `PouchApp`'s Zustand state — they're ephemeral focus / keyboard
 * plumbing, not application data, and don't need cross-render
 * subscriptions. The shortcut hooks read the current values at
 * keydown time.
 */
let _omnibarInput;
let _pendingFocusOnNextRegister;
function performFocus(handle, action) {
    handle.focus();
    if (action === "focus-and-select" && handle.isEmpty() === false) {
        handle.selectAll();
    }
}
// ---------------------------------------------------------------------------
// Input registration
// ---------------------------------------------------------------------------
/**
 * Register the omnibar's draft input handle (called by `<DraftBar>`).
 * Accepts any object satisfying the small `OmnibarInputHandle`
 * interface so the bar can swap the underlying element (textarea vs
 * Tiptap editor) per mode without churning this API.
 *
 * Two register paths matter:
 *   - Mode swap → register(newHandle): if a `focusOnNextRegister`
 *     intent is pending, it fires here against the freshly-mounted
 *     handle and the flag is cleared. This is what keeps `⌘D`/`⌘F`
 *     focused across the textarea ↔ editor remount.
 *   - True unmount → register(undefined): drops the handle AND the
 *     pending focus intent so a later remount of the bar doesn't
 *     inherit a stale focus request from before navigation.
 *
 * `<DraftBar>` is responsible for not calling `register(undefined)`
 * mid-swap (e.g., as the cleanup of a `[mode]`-keyed effect) — that
 * would clobber a pending focus intent before the new handle had a
 * chance to consume it. Today the bar pairs a `[mode]` registration
 * effect with a separate `[]`-keyed unmount cleanup precisely so the
 * pending-focus path only sees the meaningful transitions.
 */
export function registerOmnibarInput(handle) {
    _omnibarInput = handle;
    if (handle === undefined) {
        _pendingFocusOnNextRegister = undefined;
        return;
    }
    if (_pendingFocusOnNextRegister === undefined)
        return;
    const action = _pendingFocusOnNextRegister;
    _pendingFocusOnNextRegister = undefined;
    performFocus(handle, action);
}
export function getOmnibarInput() {
    return _omnibarInput;
}
/**
 * Imperatively focus the omnibar input. Caller is responsible for any
 * preceding `event.preventDefault()` so the focus lands on us rather
 * than on the next focusable element in the document order. Use this
 * when the focused input is the one you want — no mode swap is in
 * flight — so the focus call lands on a stable element.
 */
export function focusOmnibarInput() {
    _omnibarInput?.focus();
}
/**
 * Focus the omnibar input AND select all of its current value when
 * non-empty. Mirrors the browser address-bar / Find convention:
 * invoking the shortcut a second time leaves the user ready to
 * immediately overtype the existing draft rather than appending to
 * it. No-op on the value-select half when the input is empty so the
 * caret just lands at insertion position normally.
 */
export function focusAndSelectOmnibarInput() {
    const handle = _omnibarInput;
    if (handle === undefined)
        return;
    performFocus(handle, "focus-and-select");
}
/**
 * Queue a focus to fire on the **next** `registerOmnibarInput` call.
 * Use this *only* when a mode swap is being triggered in the same
 * tick — i.e. the caller has just flipped `omnibarMode` to a value
 * different from the current one, so a new handle is guaranteed to
 * register from `<DraftBar>`'s mode-keyed effect.
 *
 * Calling this without a swap pending leaves a stale flag that the
 * next unrelated mode change would pick up and produce a spurious
 * focus jump — `<DraftBar>`'s unmount cleanup clears it as a
 * safety net (see `registerOmnibarInput`'s `undefined` branch),
 * but consumers should still gate this on an actual mode change.
 */
export function focusOmnibarInputOnNextRegister() {
    _pendingFocusOnNextRegister = "focus";
}
export function focusAndSelectOmnibarInputOnNextRegister() {
    _pendingFocusOnNextRegister = "focus-and-select";
}
let _omnibarExpand;
export function registerOmnibarExpand(handle) {
    _omnibarExpand = handle;
}
export function getOmnibarExpand() {
    return _omnibarExpand;
}
// ---------------------------------------------------------------------------
// Minimize callback
// ---------------------------------------------------------------------------
let _minimize;
/**
 * Register `<DraftBar>`'s "minimize the bar" routine so external
 * surfaces (notably the Ask-mode filter strip when a result is
 * activated) can trigger a teardown without prop-drilling. Pass
 * `undefined` on unmount.
 *
 * The callback is responsible for clearing the textarea's local text
 * state, blurring any focused element inside the omnibar host,
 * resetting the stream-fade opacity, and flipping the bar back to its
 * default Add (Draft) mode — basically the inverse of "user focused
 * into the bar".
 */
export function registerOmnibarMinimize(fn) {
    _minimize = fn;
}
/**
 * Trigger the registered minimize routine. No-op when nothing is
 * registered (e.g. `<DraftBar>` isn't mounted), so callers can fire
 * unconditionally.
 */
export function minimizeOmnibar() {
    _minimize?.();
}
let _omnibarFirstResultOpener;
export function registerOmnibarFirstResultOpener(opener) {
    _omnibarFirstResultOpener = opener;
}
/**
 * Try to open the Ask-mode filter strip's top result. Returns true
 * when a result was opened (caller should `preventDefault` so the
 * default Enter behaviour doesn't also fire), false when no opener
 * was registered or the strip currently holds zero matches.
 */
export function openOmnibarFirstResult() {
    return _omnibarFirstResultOpener?.() ?? false;
}
